import React from 'react';
import {
  ClipboardCheck,
  Calendar,
  CheckCircle,
  XCircle as XCircleIcon,
  FileText,
  User,
  UserCheck,
} from 'lucide-react';
import { AuditType, User as UserType } from '../../../services/auditTypeService';

interface AuditTypeDetailViewProps {
  auditType: AuditType | null;
}

const AuditTypeDetailView: React.FC<AuditTypeDetailViewProps> = ({
  auditType,
}) => {
  if (!auditType) return null;

  const getStatusIcon = (isActive: boolean) => {
    return isActive
      ? <CheckCircle size={16} className="text-green-600" />
      : <XCircleIcon size={16} className="text-red-600" />;
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? "text-green-600 bg-green-50 border-green-200"
      : "text-red-600 bg-red-50 border-red-200";
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUserName = (user: string | UserType): string => {
    if (typeof user === 'object' && user !== null && 'name' in user) {
      return user.name;
    }
    return 'N/A';
  };

  const getUserEmail = (user: string | UserType): string => {
    if (typeof user === 'object' && user !== null && 'email' in user) {
      return user.email || 'N/A';
    }
    return 'N/A';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
          <ClipboardCheck size={24} className="text-gray-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">
            Audit Type Details
          </h2>
          <p className="text-sm text-gray-600">
            {auditType.name}
          </p>
        </div>
        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(auditType.isActive)}`}>
          {getStatusIcon(auditType.isActive)}
          <span className="ml-1.5">{auditType.isActive ? 'Active' : 'Inactive'}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="grid grid-cols-1 gap-6">
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardCheck size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Audit Type Information</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <ClipboardCheck size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Name</p>
                    <p className="text-sm text-gray-600">{auditType.name}</p>
                  </div>
                </div>

                {auditType.description && (
                  <div className="flex items-start gap-3">
                    <FileText size={16} className="text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Description</p>
                      <p className="text-sm text-gray-600">{auditType.description}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  {getStatusIcon(auditType.isActive)}
                  <div>
                    <p className="text-sm font-medium text-gray-900">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(auditType.isActive)}`}>
                      {auditType.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center gap-2 mb-4">
                <UserCheck size={20} className="text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">User Information</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created By</p>
                    <p className="text-sm text-gray-600">{getUserName(auditType.createdBy)}</p>
                    {typeof auditType.createdBy === 'object' && auditType.createdBy?.email && (
                      <p className="text-xs text-gray-500 mt-0.5">{getUserEmail(auditType.createdBy)}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <UserCheck size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Updated By</p>
                    <p className="text-sm text-gray-600">{getUserName(auditType.updatedBy)}</p>
                    {typeof auditType.updatedBy === 'object' && auditType.updatedBy?.email && (
                      <p className="text-xs text-gray-500 mt-0.5">{getUserEmail(auditType.updatedBy)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamp Information */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={20} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Timestamp Information</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Created At</p>
                    <p className="text-sm text-gray-600">{formatDate(auditType.createdAt)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Updated At</p>
                    <p className="text-sm text-gray-600">{formatDate(auditType.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTypeDetailView;
